import React, { useState, useEffect } from 'react';
import eventBus, { EVENT_NAMES } from '../../utils/event_bus';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  Pagination,
  useGetList,
  useList,
  ListContextProvider,
  FilterLiveSearch,
  SavedQueriesList,
} from 'react-admin';
import { Driver } from '../../models/Driver';
import { PlateColor, TruckLength, TruckType } from '../../utils/truck-utils';
import { 
  Box, 
  Card, 
  CardContent, 
  Chip, 
  Stack, 
  TextField as MuiTextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import zhCN from 'date-fns/locale/zh-CN';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// 定义通用的过滤器类型
type FilterType = {
  name: string;
  phone: string;
  plate: string;
  type: string;
  distance: number | null;
  lat?: number; // 地图位置纬度
  lon?: number; // 地图位置经度
  pos_from?: number; // 最后位置时间的开始时间戳
  pos_to?: number; // 最后位置时间的结束时间戳
};

// 车型选项
const truckTypeOptions = [
  { value: '', label: '全部' },
  { value: '0', label: '平板' },
  { value: '1', label: '高栏' },
  { value: '2', label: '厢式' },
  { value: '3', label: '自卸' },
  { value: '4', label: '冷藏' },
  { value: '5', label: '爬梯' },
  { value: '6', label: '集装箱' },
  { value: '7', label: '高低板' },
];

// 查询表单组件
const SearchForm: React.FC<{
  filters: FilterType;
  setFilters: React.Dispatch<React.SetStateAction<FilterType>>;
  onSearch: () => void;
  onReset: () => void;
  mapLocation: { latitude: number; longitude: number; address: string; } | null;
}> = ({ filters, setFilters, onSearch, onReset, mapLocation }) => {

  // 日期选择器通用样式
  const datePickerSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: 'white' },
    },
    '& .MuiInputBase-input': {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
    '& .MuiSvgIcon-root': { color: 'white' },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhCN}>
      <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MuiTextField
              label="姓名"
              size="small"
              fullWidth
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              InputProps={{ style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <MuiTextField
              label="手机号"
              size="small"
              fullWidth
              value={filters.phone}
              onChange={(e) => setFilters({ ...filters, phone: e.target.value })}
              InputProps={{ style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <MuiTextField
              label="车牌号"
              size="small"
              fullWidth
              value={filters.plate}
              onChange={(e) => setFilters({ ...filters, plate: e.target.value })}
              InputProps={{ style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl 
              fullWidth 
              size="small"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                },
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>车型</InputLabel>
              <Select
                value={filters.type}
                label="车型"
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                {truckTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* 日期选择器 - 只有当接收到当前位置信息时才显示 */}
          {mapLocation && (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <DatePicker
                      label="开始时间"
                      value={filters.pos_from ? new Date(filters.pos_from) : null}
                      onChange={(date) => {
                        const timestamp = date ? date.getTime() : undefined;
                        setFilters({ ...filters, pos_from: timestamp });
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          InputProps: { style: { color: 'white' } },
                          InputLabelProps: { style: { color: 'rgba(255, 255, 255, 0.7)' } },
                        }
                      }}
                      sx={datePickerSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <DatePicker
                      label="结束时间"
                      value={filters.pos_to ? new Date(filters.pos_to) : null}
                      onChange={(date) => {
                        const timestamp = date ? date.getTime() : undefined;
                        setFilters({ ...filters, pos_to: timestamp });
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          InputProps: { style: { color: 'white' } },
                          InputLabelProps: { style: { color: 'rgba(255, 255, 255, 0.7)' } },
                        }
                      }}
                      sx={datePickerSx}
                    />
                  </Box>
                </Box>
              </Grid>
            </>
          )}
          
          {/* 距离输入框和按钮在同一行 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <MuiTextField
                  label="距离(km)"
                  type="number"
                  size="small"
                  fullWidth
                  value={filters.distance === null ? '' : filters.distance}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    setFilters({ ...filters, distance: value });
                    
                    // 保存距离值到 localStorage
                    if (value !== null) {
                      localStorage.setItem('driverSearchDistance', value.toString());
                    } else {
                      localStorage.removeItem('driverSearchDistance');
                    }
                    
                    // 发布距离变化事件，通知其他页面
                    eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, value);
                  }}
                  InputProps={{ style: { color: 'white' } }}
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    '& .MuiInputBase-input': {
                      paddingTop: '6px',
                      paddingBottom: '6px',
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={onSearch}
                  startIcon={<SearchIcon />}
                  size="small"
                  sx={{ padding: '6px 16px' }}
                >
                  查询
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={onReset}
                  startIcon={<ClearIcon />}
                  size="small"
                  sx={{ 
                    color: 'white', 
                    borderColor: 'white',
                    padding: '6px 16px',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  重置
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

// 司机列表视图组件
const DriverListView: React.FC = () => {
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  // 从 localStorage 读取缓存的距离值
  const cachedDistance = localStorage.getItem('driverSearchDistance');
  
  // 添加提示消息状态
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // 计算默认的开始时间（3个月前）
  const defaultPosFrom = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date.getTime();
  };
  
  const [filters, setFilters] = useState<FilterType>({
    name: '',
    phone: '',
    plate: '',
    type: '',
    lat: undefined,
    lon: undefined,
    distance: cachedDistance ? Number(cachedDistance) : null,
    pos_from: undefined, // 默认不设置，API会使用3个月前
    pos_to: undefined // 默认不设置，API会使用当前时间
  });
  const [searchParams, setSearchParams] = useState<FilterType>({
    name: '',
    phone: '',
    plate: '',
    type: '',
    lat: undefined,
    lon: undefined,
    distance: null,
    pos_from: undefined,
    pos_to: undefined
  });
  
  // 地图位置状态
  const [mapLocation, setMapLocation] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(null);
  
  // 处理位置变化
  const handleLocationChange = (latitude: number, longitude: number, address: string) => {
    console.log('位置变化:', latitude, longitude, address);
    setMapLocation({ latitude, longitude, address });
    
    // 更新筛选条件中的经纬度，保留其他筛选条件
    const updatedFilters = {
      ...filters,
      lat: latitude,
      lon: longitude
    };
    setFilters(updatedFilters);
    
    // 从 localStorage 读取缓存的距离值
    const cachedDistance = localStorage.getItem('driverSearchDistance');
    if (cachedDistance && updatedFilters.distance === null) {
      updatedFilters.distance = Number(cachedDistance);
    }
    
    // 如果有位置信息但没有距离值，提示用户输入距离
    if (updatedFilters.distance === null) {
      setSnackbarMessage('请输入查询距离');
      setSnackbarOpen(true);
      return; // 不执行查询
    }
    
    // 自动触发查询
    const updatedParams = { 
      ...updatedFilters,
      // 在查询前将距离值乘以1000（如果存在），但不影响UI显示
      distance: updatedFilters.distance !== null ? updatedFilters.distance * 1000 : null,
      // 保留时间范围参数
      pos_from: updatedFilters.pos_from,
      pos_to: updatedFilters.pos_to
    };
    setSearchParams(updatedParams);
    setPage(1); // 重置到第一页
  };
  
  // 订阅地图位置变化事件
  useEffect(() => {
    // 订阅地图位置变化事件
    const unsubscribe = eventBus.subscribe(
      EVENT_NAMES.MAP_LOCATION_CHANGE,
      handleLocationChange
    );
    
    // 组件卸载时取消订阅
    return () => unsubscribe();
  }, []);

  // 处理查询
  const handleSearch = () => {
    // 如果有位置信息但没有距离值，提示用户输入距离
    if (mapLocation && filters.distance === null) {
      setSnackbarMessage('请输入查询距离');
      setSnackbarOpen(true);
      return; // 不执行查询
    }
    
    // 合并筛选条件和地图位置信息
    const updatedParams = { 
      ...filters,
      // 如果有地图位置信息，则加入经纬度
      lat: mapLocation?.latitude,
      lon: mapLocation?.longitude,
      // 在查询前将距离值乘以1000（如果存在），但不影响UI显示
      distance: filters.distance !== null ? filters.distance * 1000 : null,
      // 保留时间范围参数
      pos_from: filters.pos_from,
      pos_to: filters.pos_to
    };
    setSearchParams(updatedParams);
    setPage(1); // 重置到第一页
  };

  // 处理重置
  const handleReset = () => {
    const defaultFilters: FilterType = {
      name: '',
      phone: '',
      plate: '',
      type: '',
      distance: null,
      // 清除经纬度信息
      lat: undefined,
      lon: undefined,
      // 清除时间范围
      pos_from: undefined,
      pos_to: undefined
    };
    setFilters(defaultFilters);
    setSearchParams(defaultFilters);
    // 不清除地图位置状态，因为用户可能仍需要地图上的位置标记
    // 但在查询条件中已经移除了经纬度
    setPage(1); // 重置到第一页
    
    // 清除 localStorage 中缓存的距离值
    localStorage.removeItem('driverSearchDistance');
    
    // 发布距离变化事件，通知其他页面距离值已被清空
    eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, null);
  };

  // 使用 useGetList 获取分页数据
  const { data, total, isLoading } = useGetList('drivers', {
    pagination: { page, perPage },
    sort: { field: 'id', order: 'DESC' },
    filter: {
      name: searchParams.name || undefined,
      phone: searchParams.phone || undefined,
      'trucks.0.plate': searchParams.plate || undefined,
      // 传递车型名称而不是编号
      'trucks.0.type': searchParams.type ? truckTypeOptions.find(option => option.value === searchParams.type)?.label || undefined : undefined,
      // 确保使用处理后的距离值（已在handleSearch中将km转为m）
      ...(searchParams.distance !== null ? { distance: searchParams.distance } : {}),
      // 添加地图位置信息（经纬度）到查询条件
      ...(searchParams.lat && searchParams.lon ? { 
        lat: searchParams.lat,
        lon: searchParams.lon 
      } : {}),
      // 添加时间范围过滤条件
      ...(searchParams.pos_from ? { pos_from: searchParams.pos_from } : {}),
      ...(searchParams.pos_to ? { pos_to: searchParams.pos_to } : {}),
    },
  });

  // 创建列表上下文
  const listContext = useList({ data, isLoading, perPage, page });
  
  // 当司机数据更新时，发布事件通知
  useEffect(() => {
    if (data && !isLoading) {
      // 发布司机数据更新事件，将当前获取到的司机信息发送出去
      eventBus.publish(EVENT_NAMES.DRIVER_DATA_UPDATED, data);
    }
  }, [data, isLoading]);

  // 处理 Snackbar 关闭
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* 显示当前地图位置信息 */}
      {mapLocation && (
        <Box sx={{ mb: 1, p: 1, backgroundColor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1, whiteSpace: 'nowrap' }}>
              当前位置:
            </Typography>
            <Chip 
              size="small" 
              label={`${mapLocation.address || '未知地址'} (${mapLocation.latitude.toFixed(4)}, ${mapLocation.longitude.toFixed(4)})`}
              color="primary"
              variant="outlined"
              sx={{ maxWidth: '100%', overflow: 'hidden' }}
            />
          </Box>
          <IconButton 
            size="small" 
            onClick={() => {
              setMapLocation(null);
              // 如果当前查询条件中包含经纬度，则移除它们并重新查询
              if (searchParams.lat || searchParams.lon) {
                const newParams = { ...searchParams };
                delete newParams.lat;
                delete newParams.lon;
                setSearchParams(newParams);
              }
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      
      {/* 添加提示消息 */}
       <Snackbar 
         open={snackbarOpen} 
         autoHideDuration={3000} 
         onClose={handleSnackbarClose}
         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
         sx={{ marginTop: '50px' }}
       >
        <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* 查询表单 */}
      <SearchForm 
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onReset={handleReset}
        mapLocation={mapLocation}
      />
      <Box sx={{ flex: 1, width: '100%' }}>
        <ListContextProvider value={{ ...listContext, total: total || 0 }}>
          <Datagrid 
            rowClick={(id, resource, record) => {
              // 发布司机选中事件，将选中的司机信息发送出去
              console.log('司机被选中:', record);
              eventBus.publish(EVENT_NAMES.DRIVER_SELECTED, record);
              // 返回 false 阻止默认导航行为，防止触发页面重新加载
              return false;
            }} 
            bulkActionButtons={false}
            size="small"
            sx={{
              width: '100%',
              '& .RaDatagrid-headerCell': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem',
              },
              '& .RaDatagrid-row': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              },
              '& .MuiTableCell-root': {
                color: 'white',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '6px 16px',
                fontSize: '0.875rem',
              },
              '& .MuiTable-root': {
                width: '100%',
                tableLayout: 'fixed',
              },
            }}
          >
            <TextField source="name" label="姓名" />
            <TextField source="phone" label="手机号" />
            <TextField source="trucks.0.plate" label="车牌" />
            <FunctionField
              source="trucks.0.truck_type"
              label="车型"
              render={(record: Driver) => TruckType(record?.trucks?.[0]?.truck_type)}
            />
            <FunctionField
              source="trucks.0.truck_length"
              label="车长"
              render={(record: Driver) =>
                TruckLength(record?.trucks?.[0]?.truck_length)
              }
            />
          </Datagrid>
          <Pagination 
            rowsPerPageOptions={[5, 10, 15, 25]}
            page={page}
            perPage={perPage}
            setPage={setPage}
            setPerPage={setPerPage}
            sx={{
              width: '100%',
              '& .MuiToolbar-root': {
                color: 'white',
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                minHeight: '48px',
                padding: '0 8px',
              },
              '& .MuiTablePagination-root': {
                width: '100%',
              },
              '& .MuiTablePagination-select': {
                color: 'white',
              },
              '& .MuiTablePagination-selectIcon': {
                color: 'white',
              },
              '& .MuiTablePagination-displayedRows': {
                color: 'white',
                fontSize: '0.875rem',
              },
              '& .MuiIconButton-root': {
                color: 'white',
                padding: '4px',
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              },
            }}
          />
        </ListContextProvider>
      </Box>
    </Box>
  );
};

export default DriverListView;